import { extractText } from "../chat/message-extract.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatAttachment, ChatUploadedFile } from "../ui-types.ts";
import { generateUUID } from "../uuid.ts";

export type ChatState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatLoading: boolean;
  chatMessages: unknown[];
  chatThinkingLevel: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  /** 已上传的 Excel/PDF，随下一条消息以 context.file_path 发送 */
  chatUploadedFile: ChatUploadedFile | null;
  chatRunId: string | null;
  chatStream: string | null;
  chatStreamStartedAt: number | null;
  lastError: string | null;
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  state: "delta" | "final" | "aborted" | "error";
  message?: unknown;
  errorMessage?: string;
};

export type ChatEventResult = ChatEventPayload["state"] | "foreign_final" | null;

export async function loadChatHistory(state: ChatState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.chatLoading = true;
  state.lastError = null;
  try {
    const res = await state.client.request<{ messages?: Array<unknown>; thinkingLevel?: string }>(
      "chat.history",
      {
        sessionKey: state.sessionKey,
        limit: 200,
      },
    );
    const next = Array.isArray(res.messages) ? res.messages : [];
    // 避免 foreign_final 竞态：后端尚未落库时返回空，若直接覆盖会导致「session 突然消失」
    if (next.length > 0 || state.chatMessages.length === 0) {
      state.chatMessages = next;
    }
    state.chatThinkingLevel = res.thinkingLevel ?? null;
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.chatLoading = false;
  }
}

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) {
    return null;
  }
  return { mimeType: match[1], content: match[2] };
}

function normalizeAbortedAssistantMessage(message: unknown): Record<string, unknown> | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const candidate = message as Record<string, unknown>;
  if (candidate.role !== "assistant") {
    return null;
  }
  if (!("content" in candidate) || !Array.isArray(candidate.content)) {
    return null;
  }
  return candidate;
}

function hasRenderableNonTextBlocks(message: unknown): boolean {
  const m = message as Record<string, unknown>;
  const toolCalls = m.tool_calls;
  if (Array.isArray(toolCalls) && toolCalls.length > 0) {
    return true;
  }
  const content = m.content;
  if (!Array.isArray(content)) {
    return false;
  }
  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const item = block as Record<string, unknown>;
    const kind = typeof item.type === "string" ? item.type.toLowerCase() : "";
    if (
      kind === "toolcall" ||
      kind === "tool_call" ||
      kind === "tooluse" ||
      kind === "tool_use" ||
      kind === "toolresult" ||
      kind === "tool_result"
    ) {
      return true;
    }
    if (typeof item.name === "string" && item.arguments != null) {
      return true;
    }
    if (kind === "file" && typeof item.file_name === "string" && item.file_name.trim()) {
      return true;
    }
    if (kind === "image") {
      if (typeof item.url === "string" && item.url.trim()) {
        return true;
      }
      const source = item.source as Record<string, unknown> | undefined;
      if (
        source?.type === "base64" &&
        typeof source.data === "string" &&
        source.data.trim()
      ) {
        return true;
      }
    }
    if (kind === "image_url") {
      const imageUrl = item.image_url as Record<string, unknown> | undefined;
      if (typeof imageUrl?.url === "string" && imageUrl.url.trim()) {
        return true;
      }
    }
  }
  return false;
}

function shouldAppendAssistantMessage(message: unknown): boolean {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role !== "assistant") {
    return true;
  }
  if (hasRenderableNonTextBlocks(message)) {
    return true;
  }
  const visibleText = (extractText(message) ?? "").trim();
  return visibleText.length > 0;
}

/** 上传 Excel/PDF 到服务端，返回 file_path/file_name，供 chat.send 的 context 使用 */
export async function uploadChatFile(
  basePath: string,
  file: File,
): Promise<ChatUploadedFile | null> {
  const prefix = basePath?.trim() ? basePath.trim().replace(/\/$/, "") : "";
  const url = prefix ? `${prefix}/api/quotation/upload` : "/api/quotation/upload";
  const form = new FormData();
  form.append("file", file);
  try {
    const res = await fetch(url, { method: "POST", body: form, credentials: "same-origin" });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Upload failed: ${res.status}`);
    }
    const payload = (await res.json()) as { success?: boolean; data?: { file_path?: string; file_name?: string; file_id?: string; summary_meta?: ChatUploadedFile["summaryMeta"] } };
    const data = (payload?.data ?? payload) as { file_path?: string; file_name?: string; file_id?: string; summary_meta?: ChatUploadedFile["summaryMeta"] } | undefined;
    if (!data || typeof data.file_path !== "string") {
      return null;
    }
    return {
      file_id: data.file_id ?? "",
      file_path: data.file_path,
      file_name: data.file_name ?? file.name,
      summaryMeta: data.summary_meta,
    };
  } catch (err) {
    console.error("uploadChatFile", err);
    throw err;
  }
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
  uploadedFile?: ChatUploadedFile | null,
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  const hasUploadedFile = Boolean(uploadedFile?.file_name);
  if (!msg && !hasAttachments && !hasUploadedFile) {
    return null;
  }

  const now = Date.now();

  // Build user message content blocks (text + images + uploaded Excel/PDF so they show in chat)
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown; file_name?: string; file_path?: string; summaryMeta?: ChatUploadedFile["summaryMeta"] }> = [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  // Add image previews to the message for display
  if (hasAttachments) {
    for (const att of attachments) {
      contentBlocks.push({
        type: "image",
        source: { type: "base64", media_type: att.mimeType, data: att.dataUrl },
      });
    }
  }
  // Add uploaded Excel/PDF so it appears in the conversation bubble, not only above input
  if (uploadedFile?.file_name) {
    contentBlocks.push({
      type: "file",
      file_name: uploadedFile.file_name,
      file_path: uploadedFile.file_path,
      summaryMeta: uploadedFile.summaryMeta,
    });
  }

  state.chatMessages = [
    ...state.chatMessages,
    {
      role: "user",
      content: contentBlocks,
      timestamp: now,
    },
  ];

  state.chatSending = true;
  state.lastError = null;
  const runId = generateUUID();
  state.chatRunId = runId;
  state.chatStream = "";
  state.chatStreamStartedAt = now;

  // Convert attachments to API format
  const apiAttachments = hasAttachments
    ? attachments
        .map((att) => {
          const parsed = dataUrlToBase64(att.dataUrl);
          if (!parsed) {
            return null;
          }
          return {
            type: "image",
            mimeType: parsed.mimeType,
            content: parsed.content,
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null)
    : undefined;

  const context =
    uploadedFile && uploadedFile.file_path
      ? {
          file_path: uploadedFile.file_path,
          ...(uploadedFile.file_id ? { file_id: uploadedFile.file_id } : {}),
        }
      : undefined;
  try {
    await state.client.request("chat.send", {
      sessionKey: state.sessionKey,
      message: msg,
      deliver: false,
      idempotencyKey: runId,
      attachments: apiAttachments,
      ...(context ? { context, file_path: uploadedFile!.file_path } : {}),
    });
    // 发送成功后清空上传文件，避免该附件一直挂在输入框并连带后续输入
    state.chatUploadedFile = null;
    return runId;
  } catch (err) {
    const error = String(err);
    state.chatRunId = null;
    state.chatStream = null;
    state.chatStreamStartedAt = null;
    state.lastError = error;
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: "Error: " + error }],
        timestamp: Date.now(),
      },
    ];
    return null;
  } finally {
    state.chatSending = false;
  }
}

export async function abortChatRun(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  const runId = state.chatRunId;
  try {
    await state.client.request(
      "chat.abort",
      runId ? { sessionKey: state.sessionKey, runId } : { sessionKey: state.sessionKey },
    );
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export function handleChatEvent(state: ChatState, payload?: ChatEventPayload): ChatEventResult {
  if (!payload) {
    return null;
  }
  if (payload.sessionKey !== state.sessionKey) {
    return null;
  }

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // See https://github.com/openclaw/openclaw/issues/1909
  if (payload.runId && state.chatRunId && payload.runId !== state.chatRunId) {
    if (payload.state === "final") {
      return "foreign_final";
    }
    return null;
  }

  if (payload.state === "delta") {
    const next = extractText(payload.message);
    if (typeof next === "string") {
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
      }
    }
  } else if (payload.state === "final") {
    if (payload.message && shouldAppendAssistantMessage(payload.message)) {
      state.chatMessages = [
        ...state.chatMessages,
        { ...(payload.message as Record<string, unknown>), timestamp: Date.now() },
      ];
    }
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "aborted") {
    const normalizedMessage = normalizeAbortedAssistantMessage(payload.message);
    if (normalizedMessage && shouldAppendAssistantMessage(normalizedMessage)) {
      state.chatMessages = [...state.chatMessages, normalizedMessage];
    } else {
      const streamedText = state.chatStream ?? "";
      const visibleStreamedText = streamedText.trim();
      if (visibleStreamedText) {
        state.chatMessages = [
          ...state.chatMessages,
          {
            role: "assistant",
            content: [{ type: "text", text: visibleStreamedText }],
            timestamp: Date.now(),
          },
        ];
      }
    }
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    state.lastError = payload.errorMessage ?? "chat error";
  }
  return payload.state;
}
